import { useEffect, useRef } from "react";

/**
 * Keeps the screen awake using the Screen Wake Lock API (Chrome/Edge desktop & Android).
 * Auto-reacquires after visibility changes (tab restore) which can drop the lock.
 *
 * No-op on unsupported browsers (Safari < 16.4, Firefox) — the OS sleep settings
 * still apply, so it's good to combine with a kiosk OS configuration.
 */
export const useWakeLock = (enabled = true) => {
  const lockRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return undefined;

    let cancelled = false;

    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          await lock.release().catch(() => {});
          return;
        }
        lockRef.current = lock;
        lock.addEventListener("release", () => {
          // Will be reacquired on visibility change if applicable
          lockRef.current = null;
        });
      } catch {
        /* permission denied or hardware error — silent */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !lockRef.current) acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (lockRef.current) {
        lockRef.current.release().catch(() => {});
        lockRef.current = null;
      }
    };
  }, [enabled]);
};
